import { IconCollection } from "components/Icon/IconCollection";
import IconButton from "components/IconButton";
import RecordLabel from "components/RecordLabel";
import { resourceByName } from "config/resources";
import { FeResourceType } from "config/config";
import { LocationPickerPopover } from "containers/Resources/components/CommonActions/MoveToLocation/LocationPickerPopover";
import { moveToLocationAction } from "containers/Resources/components/CommonActions/MoveToLocation/MoveToLocationForm";
import { useMoveToLocation } from "containers/Resources/components/CommonActions/MoveToLocation/useMoveToLocation";
import { getWorkspaceData } from "containers/Workspaces/Workspaces.selectors";
import { Resource } from "graphql/generated/graphql";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getRelativeUrl } from "utils/service";
import { getRecordLocationId } from "utils/utils";
import "./MoveToLocation.scss";

interface MoveSingleResourceToLocationActionProps {
    resourceName: FeResourceType;
    resourceData: Resource;
    isDisabled?: boolean;
    iri?: string;
    afterAction?: () => void;
}

type MoveToSingleResourceToLocationProps = MoveSingleResourceToLocationActionProps & {
    isOpen: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    iri?: string;
    afterAction?: () => void;
};

const LocationInfo = ({ data }: { data: Resource }) => {
    const { project_id_project, bundle_id_bundle, workspace_id_workspace } = data;
    // List queries do not carry the workspace icon; it lives in the store.
    const workspace = useSelector(getWorkspaceData);
    if (project_id_project || bundle_id_bundle || workspace_id_workspace) {
        const location = project_id_project ?? bundle_id_bundle ?? workspace_id_workspace;
        if (!location) {
            return null;
        }
        const locationName = project_id_project
            ? "project"
            : bundle_id_bundle
            ? "bundle"
            : "workspace";
        return (
            <RecordLabel
                link={getRelativeUrl(locationName, location.id)}
                name={location.name}
                image={location === workspace_id_workspace ? workspace?.icon : location.icon}
                icon={resourceByName[locationName].icon}
            />
        );
    } else {
        return null;
    }
};

export const MoveSingleResourceToLocationAction = ({
    resourceName,
    resourceData,
    isDisabled,
    iri,
    afterAction,
}: MoveSingleResourceToLocationActionProps) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <>
            <MoveSingleResourceToLocation
                isOpen={isOpen}
                resourceName={resourceName}
                resourceData={resourceData}
                iri={iri}
                afterAction={afterAction}
                onConfirm={() => {
                    setIsOpen(false);
                }}
                onCancel={() => {
                    setIsOpen(false);
                }}
            />
            <div className="locationInfoContainer">
                <LocationInfo data={resourceData} />
                <IconButton
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsOpen(true);
                    }}
                    disabled={isDisabled}
                    icon={IconCollection.folderMoveOutlineIcon}
                />
            </div>
        </>
    );
};

const MoveSingleResourceToLocation = ({
    resourceName,
    resourceData,
    isOpen,
    onConfirm,
    onCancel,
    iri,
    afterAction,
}: MoveToSingleResourceToLocationProps) => {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const recordLocationId = getRecordLocationId(resourceData);

    const { selectedLocation, changeValue, options, isSameLocation } =
        useMoveToLocation(resourceName);

    const moveResource = () => {
        if (selectedLocation) {
            setIsLoading(true);
            moveToLocationAction({
                dispatch,
                resourceName,
                id: resourceData.id,
                selectedLocation,
                iri,
            })
                ?.then(() => afterAction?.())
                ?.then(() => onConfirm())
                ?.catch(console.error)
                ?.finally(() => {
                    setIsLoading(false);
                });
        }
    };

    useEffect(() => {
        if (!isOpen && selectedLocation) {
            changeValue("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    return (
        <LocationPickerPopover
            isOpen={isOpen}
            title="Move selected resource to:"
            confirmLabel="Move"
            confirmDisabled={!selectedLocation || isSameLocation}
            isLoading={isLoading}
            onConfirm={moveResource}
            onCancel={onCancel}
            name="location"
            value={selectedLocation?.value}
            changeValue={changeValue}
            options={options}
            recordLocationId={recordLocationId}
        />
    );
};
